import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    ArrowRight,
    MapPin,
    Briefcase,
    Heart,
    Shield,
    Sparkles,
    Upload,
    Check,
    FileText,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import T from "../components/EditableText";
import ConsentCheckbox from "../components/ConsentCheckbox";
import { MAX_CV_FILE_SIZE_BYTES, KILOBYTE } from "../lib/constants";

const HERO_BG =
    "https://images.pexels.com/photos/10041272/pexels-photo-10041272.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const BENEFITS = [
    { icon: Shield, title: "100% diskret", text: "Ihr aktueller Arbeitgeber erfährt nichts. Garantiert." },
    { icon: Heart, title: "Echte Beratung", text: "Wir denken über die nächste Rolle hinaus — Ihre Karriere ist ein Marathon." },
    { icon: Sparkles, title: "Top-Unternehmen", text: "Zugang zu Rollen, die nie öffentlich ausgeschrieben werden." },
    { icon: Briefcase, title: "Verhandlungs-Coaching", text: "Wir holen für Sie 15–30% mehr aus dem Angebot heraus." },
];

const STEPS = [
    { n: "01", title: "Bewerbung", text: "5 Min. ausfüllen, CV hochladen." },
    { n: "02", title: "Erstgespräch", text: "30 Min. mit einem unserer Berater." },
    { n: "03", title: "Match", text: "Wir präsentieren Ihnen 2–3 passende Rollen." },
    { n: "04", title: "Interviews", text: "Wir bereiten Sie auf jeden Schritt vor." },
    { n: "05", title: "Unterschrift", text: "Sie verhandeln, wir coachen. Bis zum Closing." },
];

export default function Kandidaten() {
    const [jobs, setJobs] = useState([]);
    const [form, setForm] = useState({ name: "", email: "", position: "", desired_salary: "", phone: "", message: "" });
    const [file, setFile] = useState(null);
    const [step, setStep] = useState(1);
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    }, []);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_CV_FILE_SIZE_BYTES) {
            toast.error("Datei zu groß (max. 10 MB)");
            return;
        }
        setFile(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Bitte einen Lebenslauf hochladen.");
            return;
        }
        if (!consent) {
            toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
            return;
        }
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name);
            fd.append("email", form.email);
            fd.append("position", form.position);
            fd.append("phone", form.phone);
            fd.append("message", form.message ? `Wunschgehalt: ${form.desired_salary || "—"}\n\n${form.message}` : `Wunschgehalt: ${form.desired_salary || "—"}`);
            fd.append("cv", file);
            await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setDone(true);
            toast.success("Bewerbung erhalten!");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const canNext = (s) => {
        if (s === 1) return form.name && form.email;
        if (s === 2) return form.position;
        if (s === 3) return !!file;
        return true;
    };

    return (
        <div data-testid="kandidaten-page">
            {/* HERO */}
            <section className="relative bg-aimploy-dark text-white overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-r from-aimploy-dark via-aimploy-dark/90 to-transparent" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-32 lg:pt-32 lg:pb-40">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs uppercase tracking-[0.2em] font-semibold backdrop-blur-md">
                            <T k="kandidaten.hero.badge">Für Talente</T>
                        </span>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] mt-8">
                            <T k="kandidaten.hero.line1">Ihre nächste Rolle</T>
                            <br />
                            <T k="kandidaten.hero.line2" className="text-aimploy-violet italic font-medium">findet Sie.</T>
                        </h1>
                        <T k="kandidaten.hero.subtitle" as="p" className="text-lg lg:text-xl text-white/70 max-w-xl mt-8 leading-relaxed">
                            Sie scrollen LinkedIn? Falscher Ansatz. Wir präsentieren Ihnen 2–3 Rollen, die wirklich passen — bei Unternehmen, die Ihre Skills brauchen.
                        </T>
                        <div className="flex flex-col sm:flex-row gap-4 mt-10">
                            <a
                                href="#bewerben"
                                data-testid="kandidaten-hero-cta"
                                className="inline-flex items-center justify-center gap-2 bg-aimploy-violet hover:bg-aimploy-violet-hover text-white px-8 py-4 rounded-full text-base font-semibold transition-all hover:scale-[1.02]"
                            >
                                <T k="kand.hero.cta_primary">Jetzt bewerben</T>
                                <ArrowUpRight className="w-5 h-5" />
                            </a>
                            <a
                                href="#jobs"
                                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 px-8 py-4 rounded-full text-base font-semibold backdrop-blur-md"
                            >
                                <T k="kand.hero.cta_secondary">Offene Positionen</T>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl mb-16">
                        <T k="kand.benefits.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Warum aimploy</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="kand.benefits.title">{`Karriere-Berater.\nNicht CV-Schubser.`}</T>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {BENEFITS.map((b, i) => (
                            <div key={b.title} className="bg-white rounded-2xl p-8 border border-black/5 hover:-translate-y-1 transition-all">
                                <div className="h-12 w-12 rounded-xl bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center mb-6">
                                    <b.icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-display text-xl font-bold mb-2"><T k={`kand.benefit.${i}.title`}>{b.title}</T></h3>
                                <T k={`kand.benefit.${i}.text`} as="p" className="text-aimploy-muted text-sm leading-relaxed">{b.text}</T>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* JOBS */}
            <section id="jobs" className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
                        <div className="lg:col-span-8">
                            <T k="kand.jobs.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Aktuelle Rollen</T>
                            <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                                <T k="kand.jobs.title">{`Sortierte Auswahl.\nKeine Massenanzeigen.`}</T>
                            </h2>
                        </div>
                        <T k="kand.jobs.description" as="p" className="lg:col-span-4 text-aimploy-muted">
                            Das ist nur ein kleiner Auszug. 80% unserer Rollen sind nicht öffentlich.
                        </T>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="jobs-grid">
                        {jobs.map((j) => (
                            <div
                                key={j.id}
                                data-testid={`job-card-${j.id}`}
                                className="group bg-aimploy-bg hover:bg-aimploy-navy hover:text-white rounded-2xl p-8 border border-transparent transition-all hover:-translate-y-1 cursor-pointer"
                                onClick={() => {
                                    setForm((f) => ({ ...f, position: j.title }));
                                    document.getElementById("bewerben")?.scrollIntoView({ behavior: "smooth" });
                                }}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex flex-wrap gap-2">
                                        {j.tags?.slice(0, 2).map((t) => (
                                            <span key={t} className="px-2.5 py-1 rounded-full text-xs font-medium bg-aimploy-violet/10 text-aimploy-violet group-hover:bg-aimploy-violet/30 group-hover:text-white">
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    <ArrowUpRight className="w-5 h-5 opacity-50 group-hover:opacity-100 group-hover:rotate-45 transition-all" />
                                </div>
                                <h3 className="font-display text-xl lg:text-2xl font-bold mb-3">{j.title}</h3>
                                <p className="text-sm opacity-70 mb-6 line-clamp-2">{j.description}</p>
                                <div className="flex flex-wrap gap-4 text-xs">
                                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                                    <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> {j.type}</span>
                                </div>
                                <div className="mt-4 pt-4 border-t border-black/5 group-hover:border-white/15 font-display font-bold">
                                    {j.salary_range}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS TIMELINE */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl mb-16">
                        <T k="kand.process.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Bewerbungsprozess</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="kand.process.title">{`Von Bewerbung\nzur Unterschrift.`}</T>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        {STEPS.map((s, i) => (
                            <div key={s.n} className="relative bg-white rounded-2xl p-6 border border-black/5">
                                <div className="font-display text-3xl font-bold text-aimploy-violet/40 mb-3">{s.n}</div>
                                <h3 className="font-display text-lg font-bold mb-2"><T k={`kand.step.${i}.title`}>{s.title}</T></h3>
                                <T k={`kand.step.${i}.text`} as="p" className="text-sm text-aimploy-muted">{s.text}</T>
                                {i < STEPS.length - 1 && (
                                    <ArrowRight className="hidden md:block absolute top-1/2 -right-3 w-5 h-5 text-aimploy-violet/30 -translate-y-1/2" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* APPLICATION FORM */}
            <section id="bewerben" className="py-24 lg:py-32 bg-aimploy-dark text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-dots-dark opacity-30" />
                <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-aimploy-violet/20 blur-3xl" />
                <div className="relative max-w-[900px] mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <span className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Bewerben</span>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            5 Minuten. Ihr<br /> nächster Schritt.
                        </h2>
                    </div>

                    {done ? (
                        <div className="bg-white text-aimploy-navy rounded-3xl p-12 text-center" data-testid="application-success">
                            <div className="h-16 w-16 rounded-full bg-aimploy-violet text-white flex items-center justify-center mx-auto mb-6">
                                <Check className="w-7 h-7" />
                            </div>
                            <h3 className="font-display text-3xl font-bold mb-3">Geschafft.</h3>
                            <p className="text-aimploy-muted max-w-md mx-auto">
                                Wir melden uns innerhalb von 48 Stunden mit einem Erstgespräch-Termin bei Ihnen.
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={submit}
                            className="bg-white text-aimploy-navy rounded-3xl p-8 lg:p-12"
                            data-testid="application-form"
                        >
                            {/* Progress */}
                            <div className="flex items-center gap-2 mb-10">
                                {[1, 2, 3, 4].map((s) => (
                                    <div key={s} className="flex-1 h-1.5 rounded-full bg-aimploy-bg overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-500 ${
                                                s <= step ? "bg-aimploy-violet w-full" : "w-0"
                                            }`}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold mb-6">
                                Schritt {step} von 4
                            </div>

                            {step === 1 && (
                                <div className="space-y-5">
                                    <h3 className="font-display text-2xl font-bold">Wer sind Sie?</h3>
                                    <input
                                        name="name"
                                        placeholder="Vor- und Nachname"
                                        value={form.name}
                                        onChange={onChange}
                                        required
                                        data-testid="application-name"
                                        className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5"
                                    />
                                    <input
                                        name="email"
                                        type="email"
                                        placeholder="E-Mail"
                                        value={form.email}
                                        onChange={onChange}
                                        required
                                        data-testid="application-email"
                                        className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5"
                                    />
                                    <input
                                        name="phone"
                                        type="tel"
                                        placeholder="Telefon (optional)"
                                        value={form.phone}
                                        onChange={onChange}
                                        className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5"
                                    />
                                </div>
                            )}

                            {step === 2 && (
                                <div className="space-y-5">
                                    <h3 className="font-display text-2xl font-bold">Welche Rolle?</h3>

                                    {/* Job-Dropdown */}
                                    {jobs.length > 0 && (
                                        <div>
                                            <label className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2 block">
                                                Aus offenen Stellen wählen (optional)
                                            </label>
                                            <select
                                                value={jobs.some((j) => j.title === form.position) ? form.position : ""}
                                                onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                                                data-testid="application-position-select"
                                                className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5 bg-white"
                                            >
                                                <option value="">— Andere Position / Initiativbewerbung —</option>
                                                {jobs.map((j) => (
                                                    <option key={j.id} value={j.title}>
                                                        {j.title} · {j.location}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2 block">
                                            Position *
                                        </label>
                                        <input
                                            name="position"
                                            placeholder="z.B. Senior Frontend Engineer"
                                            value={form.position}
                                            onChange={onChange}
                                            required
                                            data-testid="application-position"
                                            className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2 block">
                                            Wunschgehalt (brutto / Jahr)
                                        </label>
                                        <input
                                            name="desired_salary"
                                            placeholder="z.B. 85.000 € oder 80–95k"
                                            value={form.desired_salary}
                                            onChange={onChange}
                                            data-testid="application-salary"
                                            className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5"
                                        />
                                    </div>

                                    <textarea
                                        name="message"
                                        placeholder="Was suchen Sie? (optional)"
                                        value={form.message}
                                        onChange={onChange}
                                        rows={4}
                                        className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5 resize-none"
                                    />
                                </div>
                            )}

                            {step === 3 && (
                                <div className="space-y-5">
                                    <h3 className="font-display text-2xl font-bold">Lebenslauf hochladen</h3>
                                    <label
                                        htmlFor="cv"
                                        className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-aimploy-navy/15 hover:border-aimploy-violet rounded-2xl p-12 cursor-pointer transition-colors"
                                        data-testid="application-cv-dropzone"
                                    >
                                        {file ? (
                                            <>
                                                <FileText className="w-8 h-8 text-aimploy-violet" />
                                                <div className="font-semibold">{file.name}</div>
                                                <div className="text-xs text-aimploy-muted">{(file.size / KILOBYTE).toFixed(0)} KB · Klicken zum Wechseln</div>
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-8 h-8 text-aimploy-violet" />
                                                <div className="font-semibold">CV hochladen</div>
                                                <div className="text-xs text-aimploy-muted">PDF, DOC, DOCX · max. 10 MB</div>
                                            </>
                                        )}
                                        <input
                                            id="cv"
                                            type="file"
                                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={onFileChange}
                                            className="hidden"
                                            data-testid="application-cv-input"
                                        />
                                    </label>
                                </div>
                            )}

                            {step === 4 && (
                                <div className="space-y-4">
                                    <h3 className="font-display text-2xl font-bold">Alles richtig?</h3>
                                    <div className="bg-aimploy-bg rounded-xl p-5 space-y-2 text-sm">
                                        <div><span className="text-aimploy-muted">Name:</span> <strong>{form.name}</strong></div>
                                        <div><span className="text-aimploy-muted">E-Mail:</span> <strong>{form.email}</strong></div>
                                        <div><span className="text-aimploy-muted">Position:</span> <strong>{form.position}</strong></div>
                                        {form.desired_salary && <div><span className="text-aimploy-muted">Wunschgehalt:</span> <strong>{form.desired_salary}</strong></div>}
                                        <div><span className="text-aimploy-muted">CV:</span> <strong>{file?.name}</strong></div>
                                    </div>
                                    <div className="bg-white border border-aimploy-navy/10 rounded-xl p-4">
                                        <ConsentCheckbox
                                            checked={consent}
                                            onChange={setConsent}
                                            testid="application-consent"
                                            text={
                                                <>
                                                    Ich willige ein, dass meine Bewerbungsdaten inklusive Lebenslauf gemäß{" "}
                                                    <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-aimploy-violet underline hover:no-underline">
                                                        Datenschutzerklärung
                                                    </a>
                                                    {" "}verarbeitet und ggf. mit meiner gesonderten Zustimmung an passende Unternehmen weitergeleitet werden. Widerruf jederzeit per Mail möglich.
                                                </>
                                            }
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 mt-10">
                                {step > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => setStep(step - 1)}
                                        data-testid="application-back"
                                        className="flex-1 px-6 py-3.5 rounded-xl border border-aimploy-navy/15 font-semibold hover:bg-aimploy-bg"
                                    >
                                        Zurück
                                    </button>
                                )}
                                {step < 4 ? (
                                    <button
                                        type="button"
                                        onClick={() => canNext(step) && setStep(step + 1)}
                                        disabled={!canNext(step)}
                                        data-testid="application-next"
                                        className="flex-1 bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-6 py-3.5 rounded-xl font-semibold disabled:opacity-40 transition-colors"
                                    >
                                        Weiter
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="application-submit"
                                        className="flex-1 bg-aimploy-violet hover:bg-aimploy-violet-hover text-white px-6 py-3.5 rounded-xl font-semibold disabled:opacity-60 transition-colors"
                                    >
                                        {loading ? "Wird gesendet..." : "Bewerbung abschicken"}
                                    </button>
                                )}
                            </div>
                        </form>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
                    <h2 className="font-display text-5xl lg:text-7xl font-bold leading-[0.95] max-w-4xl mx-auto">
                        <T k="kand.cta.title">{`Lieber erst\nkennenlernen?`}</T>
                    </h2>
                    <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2 mt-10 bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-10 py-5 rounded-full text-base font-semibold hover:scale-[1.02] transition-all"
                    >
                        <T k="kand.cta.button">Vertrauliches Gespräch</T>
                        <ArrowUpRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
